import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useConfirmDialogState } from '../contexts/ConfirmDialogContext'
import { Button } from './ui/button'
import { cn } from '../lib/utils'

export default function ConfirmDialog() {
  const { t } = useTranslation()
  const { state, confirmAction, cancelAction } = useConfirmDialogState()
  const { open, config } = state
  const confirmBtnRef = useRef(null)

  // Auto-focus the confirm button on open + ESC closes
  useEffect(() => {
    if (!open) return undefined
    confirmBtnRef.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') cancelAction()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, cancelAction])

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return undefined
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  if (!open || !config || typeof document === 'undefined') return null

  const {
    title,
    description,
    confirmLabel = t('common.confirm'),
    cancelLabel = t('common.cancel'),
    variant = 'default',
    icon: Icon,
  } = config

  const isDestructive = variant === 'destructive'

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in"
    >
      {/* Backdrop */}
      <div
        onClick={cancelAction}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm rounded-lg border border-border bg-card text-card-foreground shadow-2xl">
        <button
          onClick={cancelAction}
          aria-label={cancelLabel}
          className="absolute top-3 end-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            {Icon && (
              <div
                className={cn(
                  'rounded-full p-2 flex-shrink-0',
                  isDestructive
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    : 'bg-app-primary-100 dark:bg-app-primary-900/30 text-app-primary-600 dark:text-app-primary-400'
                )}
              >
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0 pe-6">
              <h3
                id="confirm-dialog-title"
                className="text-base font-semibold text-foreground"
              >
                {title}
              </h3>
              {description && (
                <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant="outline"
              onClick={cancelAction}
              className="w-full sm:w-auto"
            >
              {cancelLabel}
            </Button>
            <Button
              ref={confirmBtnRef}
              onClick={confirmAction}
              className={cn(
                'w-full sm:w-auto text-white',
                isDestructive
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-app-primary-500 hover:bg-app-primary-600'
              )}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
